import classNames from 'classnames';
import * as React from 'react';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import type { BookmarkTabsVariant } from '~/Partials/BookmarkTabs/BookmarkTabsVariant';
import type { TabKey } from '~/Partials/TabList/Tab';
import { Tab } from '~/Partials/TabList/Tab';
import { TabList } from '~/Partials/TabList/TabList';
import { useHistoryState } from '~/Utils/Hooks/useHistoryState';
import type { Theme } from '~/Utils/Theme';
import styles from './BookmarkTabs.module.css';

export interface BookmarkTabProps {
    uniqueId: number;
    label: string;
}

export interface BookmarkTabsProps {
    label: string;
    tabs: BookmarkTabProps[];
    currentlySelectedTab: TabKey;
    onSelect: (key: TabKey) => void;
    variant?: BookmarkTabsVariant;
    theme?: Pick<Theme, 'withRoundedCorners'>;
}

export const BookmarkTabs: React.FC<BookmarkTabsProps> = props => {
    const contextualTheme = useTheme();

    const { label, tabs, onSelect, currentlySelectedTab, variant = 'dark', theme = contextualTheme } = props;

    useHistoryState(`BookmarkTabs-${label}`, currentlySelectedTab, onSelect);

    const { withRoundedCorners } = theme;

    const renderBookmarkTabs = (tabProps: BookmarkTabProps) => {
        const { label: tabLabel, uniqueId } = tabProps;

        const isSelected = uniqueId === currentlySelectedTab;

        return (
            <Tab
                key={uniqueId}
                tabKey={uniqueId}
                className={classNames(styles.Tab, isSelected && styles['Tab--selected'])}
                selected={isSelected}
                onSelect={onSelect}
            >
                <span className={styles.TabLabel}>{tabLabel}</span>
            </Tab>
        );
    };

    return (
        <div
            className={classNames(
                styles.BookmarkTabs,
                withRoundedCorners && styles['Variant--rounded'],
                styles[`Variant--${variant}`]
            )}
        >
            <TabList name={label} className={styles.Tabs}>
                {tabs.map(renderBookmarkTabs)}
            </TabList>
        </div>
    );
};
